import { z } from 'zod';
import { PlayerProfile } from './player';
import { Provenance } from './provenance';

/**
 * Contract for the Omni-Torn backend's HTTP API. The website, extension and
 * Discord bot all validate responses against these schemas, so a backend change
 * that breaks a client fails loudly in tests instead of silently in the UI.
 */

export const ApiErrorBody = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

/** Machine-readable error codes returned by our API (not Torn's numeric codes). */
export const ApiErrorCode = {
  BadRequest: 'bad_request',
  NotFound: 'not_found',
  PlayerNotFound: 'player_not_found',
  RateLimited: 'rate_limited',
  UpstreamUnavailable: 'upstream_unavailable',
  NotConfigured: 'not_configured',
  Internal: 'internal_error',
} as const;
export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

export const PlayerResponse = z.object({
  player: PlayerProfile,
  provenance: Provenance,
  /** Present when we served stored data because a refresh failed. */
  warning: z.string().optional(),
});
export type PlayerResponse = z.infer<typeof PlayerResponse>;

export const PlayerHistoryEntry = z.object({
  observedAt: z.number().int(),
  source: z.string(),
  level: z.number().int(),
  name: z.string(),
  factionId: z.number().int().nullable(),
  rank: z.string().nullable(),
  awards: z.number().int().nullable(),
});
export type PlayerHistoryEntry = z.infer<typeof PlayerHistoryEntry>;

export const PlayerHistoryResponse = z.object({
  playerId: z.number().int(),
  entries: z.array(PlayerHistoryEntry),
});
export type PlayerHistoryResponse = z.infer<typeof PlayerHistoryResponse>;

export const HealthResponse = z.object({
  ok: z.boolean(),
  version: z.string(),
  database: z.boolean(),
  tornApiConfigured: z.boolean(),
  /** 'mock' means data comes from the offline fake Torn API and is made up. */
  dataMode: z.enum(['live', 'mock']),
  /** Where browsers ask the Torn API (the website calls it directly with the player's own key). */
  tornApi: z.string().optional(),
});
export type HealthResponse = z.infer<typeof HealthResponse>;

export const PlayerSearchResult = z.object({
  id: z.number().int(),
  name: z.string(),
  level: z.number().int().nullable(),
  factionId: z.number().int().nullable(),
});
export type PlayerSearchResult = z.infer<typeof PlayerSearchResult>;

export const PlayerSearchResponse = z.object({
  results: z.array(PlayerSearchResult),
  /** True when Torn's own search was consulted (not just names we already knew). */
  searchedTorn: z.boolean(),
  warning: z.string().optional(),
});
export type PlayerSearchResponse = z.infer<typeof PlayerSearchResponse>;
